// src/components/team-messages/FullPageConvList.tsx
import { useState, useEffect, type RefObject } from 'react';
import { Search, Hash, MessageSquare, Users, Plus, AtSign, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TeamConversation, OrgMember } from '@/types/teamMessages';
import type { RailSection } from './FullPageRail';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  if (!iso) return '';
  const d    = new Date(iso);
  const now  = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (diff === 0) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diff < 7)   return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getDmOtherMember(conv: TeamConversation, currentUserId: string) {
  return conv.members.find((m) => m.userId !== currentUserId) ?? null;
}

// ─── props ───────────────────────────────────────────────────────────────────

interface FullPageConvListProps {
  section: RailSection;
  conversations: TeamConversation[];
  orgMembers: OrgMember[];
  currentUserId: string;
  activeConversationId: string | null;
  updatesChannelId: string | null;
  loadingConversations: boolean;
  canCreate: boolean;
  onSelectConversation: (id: string) => void;
  onStartDM: (userId: string) => void;
  onNewGroup: () => void;
  searchRef?: RefObject<HTMLInputElement>;
}

// ─── section config ──────────────────────────────────────────────────────────

const SECTION_META: Record<RailSection, { label: string; Icon: React.ElementType }> = {
  channels: { label: 'Channels',        Icon: Hash },
  dms:      { label: 'Direct Messages', Icon: MessageSquare },
  groups:   { label: 'Groups',          Icon: Users },
};

// ─── component ──────────────────────────────────────────────────────────────

export function FullPageConvList({
  section,
  conversations,
  orgMembers,
  currentUserId,
  activeConversationId,
  updatesChannelId,
  loadingConversations,
  canCreate,
  onSelectConversation,
  onStartDM,
  onNewGroup,
  searchRef,
}: FullPageConvListProps) {
  const [search,      setSearch]      = useState('');
  const [unreadsOnly, setUnreadsOnly] = useState(false);

  const q = search.toLowerCase().trim();

  useEffect(() => { setSearch(''); }, [section]);

  const { label: sectionLabel } = SECTION_META[section];

  const allConvs = conversations;

  const channels = allConvs.filter((c) => c.type === 'channel' || c.type === 'updates');
  const groups   = allConvs.filter((c) => c.type === 'group');

  const dmByUserId = new Map<string, TeamConversation>();
  for (const conv of allConvs.filter((c) => c.type === 'dm')) {
    const other = getDmOtherMember(conv, currentUserId);
    if (other) dmByUserId.set(other.userId, conv);
  }

  return (
    <div className="w-[300px] shrink-0 border-r border-[#2e2e2e] flex flex-col h-full bg-[#101010]">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 shrink-0 border-b border-[#2e2e2e]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-white">{sectionLabel}</span>
          <div className="flex items-center gap-1">
            {/* Unread toggle */}
            <button
              onClick={() => setUnreadsOnly((v) => !v)}
              title="Unreads only"
              className={cn(
                'h-6 px-2 rounded text-[10px] font-medium transition-colors',
                unreadsOnly
                  ? 'bg-[#2e2e2e] text-white'
                  : 'text-[#5a5a5a] hover:text-[#fafafa] hover:bg-[#1e1e1e]',
              )}
            >
              Unreads
            </button>
            {section === 'groups' && canCreate && (
              <button
                onClick={onNewGroup}
                title="New Group"
                className="w-6 h-6 flex items-center justify-center rounded text-[#5a5a5a] hover:text-white hover:bg-[#1e1e1e] transition-colors"
              >
                <Plus className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5a5a5a] pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            placeholder={`Search ${sectionLabel.toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-7 pl-8 pr-3 text-[11px] bg-[#1e1e1e] rounded border border-[#2e2e2e] outline-none focus:ring-1 focus:ring-white/20 placeholder:text-[#5a5a5a] text-[#fafafa]"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loadingConversations ? (
          <div className="space-y-1 px-3 py-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-[#1e1e1e] animate-pulse" />
            ))}
          </div>
        ) : section === 'channels' ? (
          <ChannelList
            channels={channels}
            updatesChannelId={updatesChannelId}
            activeId={activeConversationId}
            search={q}
            unreadsOnly={unreadsOnly}
            onSelect={onSelectConversation}
          />
        ) : section === 'dms' ? (
          <DmList
            orgMembers={orgMembers}
            currentUserId={currentUserId}
            dmByUserId={dmByUserId}
            activeConversationId={activeConversationId}
            search={q}
            unreadsOnly={unreadsOnly}
            onSelect={onSelectConversation}
            onStartDM={onStartDM}
          />
        ) : (
          <GroupList
            groups={groups}
            activeId={activeConversationId}
            search={q}
            unreadsOnly={unreadsOnly}
            onSelect={onSelectConversation}
          />
        )}
      </div>
    </div>
  );
}

// ─── Channels sub-list ────────────────────────────────────────────────────────

function ChannelList({
  channels,
  updatesChannelId,
  activeId,
  search,
  unreadsOnly,
  onSelect,
}: {
  channels: TeamConversation[];
  updatesChannelId: string | null;
  activeId: string | null;
  search: string;
  unreadsOnly: boolean;
  onSelect: (id: string) => void;
}) {
  const updates = channels.filter((c) => c.id === updatesChannelId);
  let rest = channels
    .filter(
      (c) =>
        c.id !== updatesChannelId &&
        (!search || (c.name ?? '').toLowerCase().includes(search)),
    )
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  if (unreadsOnly) rest = rest.filter((c) => c.unreadCount > 0);

  if (updates.length === 0 && rest.length === 0) return <Empty label="No channels" />;

  return (
    <div className="py-1">
      {updates.map((c) => (
        <ConvRow
          key={c.id}
          label={c.name ?? 'updates'}
          preview={c.lastMessage?.body}
          previewSender={c.lastMessage?.senderName}
          time={c.updatedAt ? formatTime(c.updatedAt) : ''}
          unread={c.unreadCount}
          isActive={activeId === c.id}
          typeIcon="channel"
          onClick={() => onSelect(c.id)}
        />
      ))}
      {rest.map((c) => (
        <ConvRow
          key={c.id}
          label={c.name ?? 'channel'}
          preview={c.lastMessage?.body}
          previewSender={c.lastMessage?.senderName}
          time={c.updatedAt ? formatTime(c.updatedAt) : ''}
          unread={c.unreadCount}
          isActive={activeId === c.id}
          typeIcon="channel"
          onClick={() => onSelect(c.id)}
        />
      ))}
    </div>
  );
}

// ─── DMs sub-list ─────────────────────────────────────────────────────────────

function DmList({
  orgMembers,
  currentUserId,
  dmByUserId,
  activeConversationId,
  search,
  unreadsOnly,
  onSelect,
  onStartDM,
}: {
  orgMembers: OrgMember[];
  currentUserId: string;
  dmByUserId: Map<string, TeamConversation>;
  activeConversationId: string | null;
  search: string;
  unreadsOnly: boolean;
  onSelect: (id: string) => void;
  onStartDM: (userId: string) => void;
}) {
  let filtered = orgMembers.filter(
    (m) =>
      m.userId !== currentUserId &&
      (!search || m.displayName.toLowerCase().includes(search)),
  );

  if (unreadsOnly) {
    filtered = filtered.filter((m) => {
      const conv = dmByUserId.get(m.userId);
      return conv && conv.unreadCount > 0;
    });
  }

  if (filtered.length === 0) return <Empty label="No members" />;

  const sorted = [...filtered].sort((a, b) => {
    const convA = dmByUserId.get(a.userId);
    const convB = dmByUserId.get(b.userId);
    if (convA && convB) return new Date(convB.updatedAt).getTime() - new Date(convA.updatedAt).getTime();
    if (convA) return -1;
    if (convB) return 1;
    return a.displayName.localeCompare(b.displayName);
  });

  return (
    <div className="py-1">
      {sorted.map((member) => {
        const existing = dmByUserId.get(member.userId);
        const isActive = existing ? activeConversationId === existing.id : false;
        return (
          <button
            key={member.userId}
            onClick={() => (existing ? onSelect(existing.id) : onStartDM(member.userId))}
            className={cn(
              'w-full px-3 py-2.5 flex items-start gap-3 transition-colors text-left',
              isActive
                ? 'bg-[#1e1e1e] border-l-2 border-white'
                : 'hover:bg-[#1e1e1e]',
            )}
          >
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold text-white bg-[#2e2e2e] mt-0.5">
              {member.initials}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-1">
                <span
                  className={cn(
                    'text-xs truncate',
                    (existing?.unreadCount ?? 0) > 0
                      ? 'font-semibold text-white'
                      : 'font-medium text-[#fafafa]',
                  )}
                >
                  {member.displayName}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  {existing?.updatedAt && (
                    <span className="text-[9px] text-[#5a5a5a] tabular-nums">
                      {formatTime(existing.updatedAt)}
                    </span>
                  )}
                  {existing && existing.unreadCount > 0 && (
                    <span className="min-w-[16px] h-4 rounded-full bg-white text-black text-[8px] font-bold flex items-center justify-center px-1 tabular-nums">
                      {existing.unreadCount > 99 ? '99+' : existing.unreadCount}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[10px] text-[#8a8a8a] truncate mt-0.5 leading-snug">
                {existing?.lastMessage?.body ?? member.role ?? ''}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Groups sub-list ──────────────────────────────────────────────────────────

function GroupList({
  groups,
  activeId,
  search,
  unreadsOnly,
  onSelect,
}: {
  groups: TeamConversation[];
  activeId: string | null;
  search: string;
  unreadsOnly: boolean;
  onSelect: (id: string) => void;
}) {
  let filtered = groups
    .filter((g) => !search || (g.name ?? '').toLowerCase().includes(search))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  if (unreadsOnly) filtered = filtered.filter((g) => g.unreadCount > 0);

  if (filtered.length === 0) return <Empty label="No groups" />;

  return (
    <div className="py-1">
      {filtered.map((g) => (
        <ConvRow
          key={g.id}
          label={g.name ?? 'Group'}
          preview={g.lastMessage?.body}
          previewSender={g.lastMessage?.senderName}
          time={g.updatedAt ? formatTime(g.updatedAt) : ''}
          unread={g.unreadCount}
          isActive={activeId === g.id}
          typeIcon="group"
          onClick={() => onSelect(g.id)}
        />
      ))}
    </div>
  );
}

// ─── Shared activity-style row ────────────────────────────────────────────────

function ConvRow({
  label,
  preview,
  previewSender,
  time,
  unread,
  isActive,
  typeIcon,
  onClick,
}: {
  label: string;
  preview: string | undefined;
  previewSender: string | undefined;
  time: string;
  unread: number;
  isActive: boolean;
  typeIcon: 'channel' | 'group' | 'dm';
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full px-3 py-2.5 flex items-start gap-3 transition-colors text-left',
        isActive
          ? 'bg-[#1e1e1e] border-l-2 border-white'
          : 'hover:bg-[#1e1e1e]',
      )}
    >
      {/* Left indicator icon */}
      <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg mt-0.5 bg-[#2e2e2e]">
        {typeIcon === 'channel' ? (
          <AtSign className="h-3.5 w-3.5 text-[#8a8a8a]" />
        ) : typeIcon === 'group' ? (
          <Users className="h-3.5 w-3.5 text-[#8a8a8a]" />
        ) : (
          <MessageCircle className="h-3.5 w-3.5 text-[#8a8a8a]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-1">
          <span
            className={cn(
              'text-xs truncate',
              unread > 0 ? 'font-semibold text-white' : 'font-medium text-[#fafafa]',
            )}
          >
            {typeIcon === 'channel' ? `#${label}` : label}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {time && (
              <span className="text-[9px] text-[#5a5a5a] tabular-nums">{time}</span>
            )}
            {unread > 0 && (
              <span className="min-w-[16px] h-4 rounded-full bg-white text-black text-[8px] font-bold flex items-center justify-center px-1 tabular-nums">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </div>
        </div>
        {preview && (
          <p className="text-[10px] text-[#8a8a8a] truncate mt-0.5 leading-snug">
            {previewSender ? (
              <span>
                <span className="text-[#5a5a5a] font-medium">{previewSender}:</span>{' '}
                {preview}
              </span>
            ) : preview}
          </p>
        )}
      </div>
    </button>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function Empty({ label }: { label: string }) {
  return (
    <p className="px-4 py-4 text-[11px] text-[#5a5a5a]">{label}</p>
  );
}
